import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Editorial in-viewport reel of the 6 marketing clips on the landing
 * page. Each clip is rendered as an alternating-side text + video row
 * (Stripe / Linear style) — text on the left for odd rows, video on
 * the left for even rows. Avoids the AI-slop carousel feel.
 *
 * Behavior:
 *   - Hits /api/marketing-clips/manifest once to find out which clips
 *     are actually present (skips missing ones — no empty rectangles
 *     when a clip's first GH Actions run hasn't happened yet).
 *   - Each <video> autoplays muted on first entry into the viewport
 *     using IntersectionObserver. Pauses when scrolled out so we
 *     don't waste battery on mobile.
 *   - Falls back to a static placeholder if the whole manifest 404s
 *     (e.g. backend rolled back) — the section just hides itself.
 */

const FEATURE_COPY = {
  "01_register_dog": {
    eyebrow: "Register",
    title: "From paperwork to provenance in ~2 minutes.",
    body: "Fill the form, drop the photos, submit. The dog gets a registration number and a permanent record — breed, DOB, microchip, photos, the whole audit trail. No PDF graveyard, no spreadsheets.",
  },
  "02_intelligent_breeding": {
    eyebrow: "Breed smart",
    title: "Compatibility, scored before the first introduction.",
    body: "Filter the directory by breed, open a stud, run Find-matches against your female. We project the COI inbreeding coefficient and surface compatibility signals before you book a single drive across the state line.",
  },
  "03_buy_without_burned": {
    eyebrow: "Buy without getting burned",
    title: "Stripe escrow holds funds until the dog is in your hands.",
    body: "Verified kennel · 2FA · Buyer Pass on every listing. Checkout funds escrow — money releases on transfer, not before. The 'buying a dog should feel like buying a dog' beat.",
  },
  "04_transport_marketplace": {
    eyebrow: "Cross-country, vetted",
    title: "Move a dog across states without crossing your fingers.",
    body: "Open the transport directory, see vetted carriers with rigs, states served, and ratings. Request a quote, fund the trip into escrow, and watch the calendar tick from pickup to ETA to delivery confirmation.",
  },
  "05_breeder_storefront": {
    eyebrow: "Your kennel, your storefront",
    title: "Sell the way a real kennel sells — directly.",
    body: "Launch a public storefront with one click. Stripe Connect direct deposit, your slug, your photos, your prices. Buyers land on a kennel page that looks like the kennel — not a flea-market listing.",
  },
  "06_dog_log_care": {
    eyebrow: "Care for the dog you already have",
    title: "Ten care categories. One log. Pattern-aware warnings.",
    body: "Meals, walks, weight, meds, vaccinations — all in one timeline. Smart symptom warnings catch the third diarrhea entry in a week so you call the vet on Tuesday instead of Saturday at the ER.",
  },
};

const ORDER = [
  "01_register_dog",
  "02_intelligent_breeding",
  "03_buy_without_burned",
  "04_transport_marketplace",
  "05_breeder_storefront",
  "06_dog_log_care",
];

export default function MarketingClipReel() {
  const [available, setAvailable] = useState(null); // null while loading
  useEffect(() => {
    let cancelled = false;
    api
      .get("/marketing-clips/manifest")
      .then((r) => {
        if (cancelled) return;
        const next = {};
        (r.data?.clips || []).forEach((c) => {
          next[c.clip_id] = c.available;
        });
        setAvailable(next);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailable({}); // → renders nothing, section self-hides
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // While loading, render nothing — no skeleton flash. Section is
  // optional and shouldn't shift the page on every visit.
  if (available === null) return null;

  const visible = ORDER.filter((cid) => available[cid]);
  if (visible.length === 0) return null;

  return (
    <section
      className="relative py-20 md:py-28 bg-white"
      data-testid="landing-clip-reel"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-16 max-w-3xl">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">
            See the platform in motion
          </p>
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1] text-ink">
            Real footage of the real product.
          </h2>
          <p className="mt-4 text-ink/70 max-w-2xl text-base md:text-lg leading-relaxed">
            No mocks, no after-effects. Every clip below is a Playwright
            recording of the live registry — captured against the same
            interface you'll log into in a minute.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {visible.map((cid, idx) => (
            <ClipRow key={cid} clipId={cid} flip={idx % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ClipRow({ clipId, flip }) {
  const copy = FEATURE_COPY[clipId];
  const videoRef = useRef(null);
  // Cache-bust on every mount so a stale 20-byte response from a
  // mid-deploy bad pod can't poison the browser/HTTP cache and stick
  // around for the lifetime of the tab.
  const cacheBuster = useRef(Date.now());
  // `errorCount` triggers an automatic src-refresh on transient
  // failures (e.g. one pod still warming up while the others have
  // healed). After two strikes we leave the slot empty rather than
  // hammer the server, but we never render a permanent "unavailable"
  // overlay — modern browsers just show the black `<video>` element
  // background, which matches the section's brand language better
  // than apologetic gray text.
  const [errorCount, setErrorCount] = useState(0);

  // Autoplay-on-scroll using IntersectionObserver. Pause when out of
  // viewport so we don't drain battery on mobile with 6 background
  // videos. Also force a slower playback rate so the recorded flows
  // breathe — Playwright clicks faster than any human would, and at
  // 1.0× the clips feel like a speedrun.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.playbackRate = 0.75;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // play() is async + rejects if the browser blocks autoplay.
          // Swallow the rejection — without sound, modern browsers
          // allow autoplay, but we still don't want a noisy console.
          const p = el.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
  const src = `${backendUrl}/api/marketing-clips/${clipId}.mp4?v=${cacheBuster.current}-${errorCount}`;

  const handleError = () => {
    // First failure: bump the cache-buster after a 1.5s delay, which
    // re-triggers <video>'s source resolution against a fresh URL. By
    // then the backend's self-healer has had time to swap the file.
    if (errorCount < 1) {
      setTimeout(() => {
        cacheBuster.current = Date.now();
        setErrorCount((c) => c + 1);
      }, 1500);
    }
  };

  const text = (
    <div className="max-w-3xl mx-auto text-center mb-10">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">
        {copy.eyebrow}
      </p>
      <h3 className="font-spectral font-light text-2xl md:text-4xl tracking-tight leading-[1.15] text-ink">
        {copy.title}
      </h3>
      <p className="mt-5 text-ink/70 text-base md:text-lg leading-relaxed">
        {copy.body}
      </p>
    </div>
  );

  const video = (
    <div className="w-full">
      <div className="relative rounded-2xl overflow-hidden bg-ink ring-1 ring-ink/5 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.45)]">
        <video
          ref={videoRef}
          key={errorCount} /* force <video> to fully re-create on retry */
          src={src}
          muted
          loop
          playsInline
          preload="metadata"
          onError={handleError}
          className="w-full h-auto aspect-video object-cover"
          data-testid={`landing-clip-${clipId}`}
        />
        {/* Subtle gradient ring; uses transform so it doesn't trigger
            a costly repaint of the underlying video. */}
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5" />
      </div>
    </div>
  );

  // Stacked layout: text on top, full-width video below. With the
  // section's max-w-6xl container, each video renders at ~1100px wide
  // — large enough that the UI shown inside is readable instead of
  // appearing as illegibly small text crammed into a 520px card.
  // `flip` is kept in the signature for backward compatibility but no
  // longer affects layout (text is always above the video, like a
  // figure caption).
  return (
    <div>
      {text}
      {video}
    </div>
  );
}
